<template>
  <q-dialog
    :model-value="modelValue"
    persistent
    @update:model-value="emit('update:modelValue', $event)"
    @hide="onHide"
  >
    <q-card style="min-width: 800px; max-width: 90vw;">
      <q-card-section class="row items-center q-pb-none bg-purple text-white">
        <div class="text-h6">Template Manager</div>
        <q-space />
        <q-btn v-close-popup icon="close" flat round dense text-color="white" />
      </q-card-section>

      <q-card-section>
        <div v-if="loadingTemplates" class="row justify-center q-pa-md">
          <q-spinner color="primary" size="3em" />
        </div>

        <div v-else>
          <!-- Template Stats -->
          <div class="row q-gutter-md q-mb-md">
            <q-card flat bordered class="col">
              <q-card-section class="text-center">
                <div class="text-h4 text-primary">{{ availableTemplates.length }}</div>
                <div class="text-caption">Total Templates</div>
              </q-card-section>
            </q-card>
            <q-card flat bordered class="col">
              <q-card-section class="text-center">
                <div class="text-h4 text-green">{{ activeTemplateCount }}</div>
                <div class="text-caption">Active Templates</div>
              </q-card-section>
            </q-card>
            <q-card flat bordered class="col">
              <q-card-section class="text-center">
                <div class="text-h4 text-orange">{{ totalTemplateUsage }}</div>
                <div class="text-caption">Total Usage</div>
              </q-card-section>
            </q-card>
          </div>

          <!-- Search and Filter -->
          <div class="row q-gutter-md q-mb-md">
            <q-input
              v-model="internalTemplateFilter"
              placeholder="Search templates..."
              outlined
              dense
              clearable
              class="col"
            >
              <template #append>
                <q-icon name="search" />
              </template>
            </q-input>
            <q-select
              v-model="internalTemplateTypeFilter"
              :options="templateFilterTypeOptions"
              placeholder="Filter by type"
              outlined
              dense
              clearable
              emit-value
              map-options
              style="min-width: 150px"
            />
          </div>

          <!-- Templates List -->
          <q-table
            :rows="filteredTemplates"
            :columns="columns"
            row-key="id"
            dense
            flat
            bordered
            :pagination="{ rowsPerPage: 10 }"
            class="template-manager-table"
          >
            <template #body-cell-name="props">
              <q-td :props="props">
                <div class="text-weight-bold">{{ props.value }}</div>
                <div class="text-caption text-grey">{{ props.row.description }}</div>
              </q-td>
            </template>

            <template #body-cell-type="props">
              <q-td :props="props">
                <q-badge :color="getTemplateTypeColor(props.value)">
                  {{ formatTemplateType(props.value) }}
                </q-badge>
              </q-td>
            </template>

            <template #body-cell-brick_class="props">
              <q-td :props="props">
                <q-chip
                  :color="props.value ? 'positive' : 'warning'"
                  text-color="white"
                  dense
                >
                  {{ props.value || 'Not mapped' }}
                </q-chip>
              </q-td>
            </template>

            <template #body-cell-components="props">
              <q-td :props="props">
                <span v-if="props.row.template_type === 'compound'">
                  {{ props.value || 0 }}
                </span>
                <span v-else class="text-grey">-</span>
              </q-td>
            </template>

            <template #body-cell-points="props">
              <q-td :props="props">
                <span v-if="props.value > 0">
                  {{ props.value }}
                </span>
                <span v-else class="text-grey">-</span>
              </q-td>
            </template>

            <template #body-cell-usage="props">
              <q-td :props="props">
                <q-badge color="primary" outline>
                  {{ props.value }} uses
                </q-badge>
              </q-td>
            </template>

            <template #body-cell-status="props">
              <q-td :props="props">
                <q-badge :color="props.row.is_active ? 'green' : 'grey'">
                  {{ props.row.is_active ? 'Active' : 'Archived' }}
                </q-badge>
              </q-td>
            </template>

            <template #body-cell-actions="props">
              <q-td :props="props">
                <q-btn
                  v-if="props.row.template_type !== 'compound'"
                  size="sm"
                  flat
                  round
                  color="primary"
                  icon="edit"
                  @click="emit('edit-template', props.row)"
                >
                  <q-tooltip>Edit Template</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="props.row.template_type === 'compound'"
                  size="sm"
                  flat
                  round
                  color="purple"
                  icon="visibility"
                  @click="emit('view-compound-template', props.row)"
                >
                  <q-tooltip>View Compound Template</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="props.row.template_type !== 'compound'"
                  size="sm"
                  flat
                  round
                  :color="props.row.is_active ? 'grey' : 'green'"
                  :icon="props.row.is_active ? 'archive' : 'unarchive'"
                  @click="emit('toggle-template-status', props.row)"
                >
                  <q-tooltip>{{ props.row.is_active ? 'Archive' : 'Restore' }} Template</q-tooltip>
                </q-btn>
                <q-btn
                  size="sm"
                  flat
                  round
                  color="negative"
                  icon="delete"
                  @click="emit('delete-template', props.row)"
                >
                  <q-tooltip>Delete Template</q-tooltip>
                </q-btn>
                <q-btn
                  v-if="props.row.template_type !== 'compound'"
                  size="sm"
                  flat
                  round
                  color="info"
                  icon="info"
                  @click="emit('show-template-details', props.row)"
                >
                  <q-tooltip>View Details</q-tooltip>
                </q-btn>
              </q-td>
            </template>
          </q-table>

          <div v-if="filteredTemplates.length === 0" class="text-center q-pa-xl text-grey">
            <q-icon name="library_books" size="3em" class="q-mb-md" />
            <div class="text-h6">No Templates Found</div>
            <div class="text-body2">
              {{ availableTemplates.length === 0 ? 'Create templates by converting existing equipment.' : 'Try adjusting your search criteria.' }}
            </div>
          </div>
        </div>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn
          flat
          label="Refresh"
          color="primary"
          :loading="loadingTemplates"
          @click="emit('refresh')"
        />
        <q-btn v-close-popup flat label="Close" color="primary" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';

  const props = defineProps({
    modelValue: {
      type: Boolean,
      default: false,
    },
    availableTemplates: {
      type: Array,
      default: () => [],
    },
    loadingTemplates: {
      type: Boolean,
      default: false,
    },
    templateFilter: {
      type: String,
      default: '',
    },
    templateTypeFilter: {
      type: String,
      default: null,
    },
    templateFilterTypeOptions: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits([
    'update:modelValue',
    'update:template-filter',
    'update:template-type-filter',
    'hide',
    'edit-template',
    'view-compound-template',
    'toggle-template-status',
    'delete-template',
    'show-template-details',
    'refresh',
  ]);

  // Internal filter states
  const internalTemplateFilter = ref(props.templateFilter);
  const internalTemplateTypeFilter = ref(props.templateTypeFilter);

  // Watch for prop changes
  watch(() => props.templateFilter, (val) => {
    internalTemplateFilter.value = val;
  });

  watch(() => props.templateTypeFilter, (val) => {
    internalTemplateTypeFilter.value = val;
  });

  // Emit filter changes
  watch(internalTemplateFilter, (val) => {
    emit('update:template-filter', val);
  });

  watch(internalTemplateTypeFilter, (val) => {
    emit('update:template-type-filter', val);
  });

  const columns = [
    { name: 'name', label: 'Template Name', field: 'name', align: 'left', sortable: true },
    { name: 'type', label: 'Type', field: 'template_type', align: 'left', sortable: true },
    { name: 'brick_class', label: 'BRICK Class', field: 'brick_class', align: 'left', sortable: true },
    { name: 'components', label: 'Components', field: 'component_count', align: 'center', sortable: true },
    { name: 'points', label: 'Points', field: 'total_points', align: 'center', sortable: true },
    { name: 'usage', label: 'Usage Count', field: 'usage_count', align: 'center', sortable: true },
    { name: 'status', label: 'Status', field: 'is_active', align: 'center', sortable: true },
    { name: 'created', label: 'Created', field: 'created_at', align: 'left', sortable: true },
    { name: 'actions', label: 'Actions', field: 'actions', align: 'center' },
  ];

  const activeTemplateCount = computed(() => {
    return props.availableTemplates.filter((t) => t.is_active).length;
  });

  const totalTemplateUsage = computed(() => {
    return props.availableTemplates.reduce((sum, t) => sum + (t.usage_count || 0), 0);
  });

  const filteredTemplates = computed(() => {
    let templates = props.availableTemplates;

    // Apply text filter
    if (internalTemplateFilter.value) {
      const search = internalTemplateFilter.value.toLowerCase();
      templates = templates.filter((t) =>
        t.name?.toLowerCase().includes(search) ||
        t.description?.toLowerCase().includes(search) ||
        t.brick_class?.toLowerCase().includes(search),
      );
    }

    // Apply type filter
    if (internalTemplateTypeFilter.value) {
      templates = templates.filter((t) => t.template_type === internalTemplateTypeFilter.value);
    }

    return templates;
  });

  function getTemplateTypeColor(type) {
    const colors = {
      equipment: 'blue',
      device: 'green',
      compound: 'purple',
    };
    return colors[type] || 'grey';
  }

  function formatTemplateType(type) {
    const labels = {
      equipment: 'Equipment',
      device: 'Device',
      compound: 'Compound',
    };
    return labels[type] || type;
  }

  function onHide() {
    emit('hide');
  }
</script>

<style scoped>
.template-manager-table {
  border-radius: 8px;
  overflow: hidden;
}
</style>
